/**
 * Trovva — Unified Settings Page
 *
 * Horizontal tab layout consolidating all settings:
 * Profile | Organization | Team | Billing | Integrations | Knowledge Base | Notifications | Security
 *
 * Uses inline styles matching the rest of the Trovva UI (no Tailwind).
 */
import { useState, useEffect } from 'react';
import { theme } from '../../lib/theme';
import { supabase } from '../../lib/supabase';
import { formatDate } from '../../lib/dateFormat';
import KnowledgeBaseView from './KnowledgeBaseView';
import CallerIdProtection from './CallerIdProtection';
import PixelSetup from './PixelSetup';
import LinkInBioSettings from './LinkInBioSettings';
import OnboardingFlow from '../onboarding/OnboardingFlow';
import TemplateManager from './TemplateManager';

const TABS = [
  { id: 'profile', label: 'Profile & Brokerage' },
  { id: 'team', label: 'Team' },
  { id: 'billing', label: 'Billing' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'knowledge', label: 'Knowledge Base' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'security', label: 'Security' },
  { id: 'setup', label: 'Setup Wizard' },
];

// ── Shared styles ────────────────────────────────────────────
const card = {
  background: theme.colors.card,
  borderRadius: theme.radius.lg,
  border: `1px solid ${theme.colors.border}`,
  padding: 24,
  marginBottom: 20,
};
const label = { display: 'block', fontSize: 13, fontWeight: 500, color: theme.colors.textLight, marginBottom: 4 };
const input = {
  width: '100%', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm,
  padding: '8px 12px', fontSize: 14, color: theme.colors.text, background: theme.colors.card,
  outline: 'none', boxSizing: 'border-box',
};
const btnPrimary = {
  padding: '8px 20px', background: theme.colors.primary, color: '#fff', border: 'none',
  borderRadius: theme.radius.sm, fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const btnSecondary = {
  padding: '8px 16px', background: 'transparent', color: theme.colors.text,
  border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm,
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
};

function getAuthHeaders() {
  // We'll get session from supabase in each tab's useEffect
  return { 'Content-Type': 'application/json' };
}

async function getHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return null;
  return { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' };
}

// ══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════
export default function SettingsPage({ user, demoMode, org, showToast, userRole }) {
  const [activeTab, setActiveTab] = useState('profile');

  return (
    <div style={{ padding: '24px 32px', maxWidth: 960, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: theme.colors.text, marginBottom: 20 }}>Settings</h1>

      {/* Horizontal tabs */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: `2px solid ${theme.colors.border}`,
        marginBottom: 24, overflowX: 'auto',
      }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 18px', fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400,
              color: activeTab === tab.id ? theme.colors.primary : theme.colors.textLight,
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: activeTab === tab.id ? `2px solid ${theme.colors.primary}` : '2px solid transparent',
              marginBottom: -2, whiteSpace: 'nowrap', transition: 'all 0.15s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'profile' && <ProfileTab />}
      {activeTab === 'team' && <TeamTab />}
      {activeTab === 'billing' && <BillingTab />}
      {activeTab === 'integrations' && <IntegrationsTab />}
      {activeTab === 'knowledge' && <KnowledgeBaseView />}
      {activeTab === 'notifications' && <NotificationsTab />}
      {activeTab === 'security' && <SecurityTab />}
      {activeTab === 'setup' && <SetupWizardTab showToast={showToast} />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PROFILE & BROKERAGE TAB (merged)
// ══════════════════════════════════════════════════════════════
function ProfileTab() {
  const [profile, setProfile] = useState(null);
  const [org, setOrg] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);
  const [msgProfile, setMsgProfile] = useState('');
  const [msgOrg, setMsgOrg] = useState('');

  useEffect(() => {
    (async () => {
      const h = await getHeaders();
      if (!h) return;
      const [pRes, oRes] = await Promise.all([
        fetch('/api/settings?section=profile', { headers: h }).then(r => r.json()),
        fetch('/api/settings?section=org', { headers: h }).then(r => r.json()).catch(() => ({})),
      ]);
      setProfile(pRes.profile);
      setOrg(oRes.org);
    })();
  }, []);

  const saveProfile = async () => {
    setSavingProfile(true); setMsgProfile('');
    const h = await getHeaders();
    const resp = await fetch('/api/settings?section=profile', { method: 'POST', headers: h, body: JSON.stringify(profile) });
    setMsgProfile(resp.ok ? 'Saved!' : 'Error saving');
    setSavingProfile(false);
  };

  const saveOrg = async (section = 'org') => {
    setSavingOrg(true); setMsgOrg('');
    const h = await getHeaders();
    const resp = await fetch(`/api/settings?section=${section}`, { method: 'POST', headers: h, body: JSON.stringify(org) });
    setMsgOrg(resp.ok ? 'Saved!' : 'Error');
    setSavingOrg(false);
  };

  if (!profile) return <div style={{ color: theme.colors.textLight, padding: 32 }}>Loading...</div>;

  const pField = (lbl, key, type) => (
    <div>
      <label style={label}>{lbl}</label>
      <input type={type || 'text'} value={profile[key] || ''} style={input}
        onChange={e => setProfile(p => ({ ...p, [key]: e.target.value }))} />
    </div>
  );

  const oField = (lbl, key, type) => (
    <div>
      <label style={label}>{lbl}</label>
      <input type={type || 'text'} value={org?.[key] || ''} style={input}
        onChange={e => setOrg(o => ({ ...o, [key]: e.target.value }))} />
    </div>
  );

  return (
    <div>
      {/* Your Profile */}
      <div style={card}>
        <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 16 }}>Your Profile</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {pField('Full Name', 'full_name')}
          {pField('Email', 'email', 'email')}
          {pField('Phone', 'phone', 'tel')}
          {pField('Secondary Phone', 'phone_secondary', 'tel')}
          {pField('Title', 'title')}
          {pField('License Number', 'license_number')}
          {pField('Timezone', 'timezone')}
          {pField('Avatar URL', 'avatar_url', 'url')}
        </div>
        <div style={{ marginTop: 16 }}>
          <label style={label}>Bio</label>
          <textarea value={profile.bio || ''} rows={3} style={{ ...input, resize: 'vertical' }}
            onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20 }}>
          <button onClick={saveProfile} disabled={savingProfile} style={{ ...btnPrimary, opacity: savingProfile ? 0.5 : 1 }}>
            {savingProfile ? 'Saving...' : 'Save Profile'}
          </button>
          {msgProfile && <span style={{ fontSize: 13, color: msgProfile === 'Saved!' ? theme.colors.success : theme.colors.danger }}>{msgProfile}</span>}
        </div>
      </div>

      {/* Link-in-Bio */}
      <LinkInBioSettings />

      {/* Brokerage Details */}
      {org && (
        <>
          <div style={card}>
            <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 16 }}>Brokerage Details</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {oField('Brokerage Name', 'name')}
              {oField('Phone', 'phone', 'tel')}
              {oField('Website', 'website', 'url')}
              {oField('Address', 'address')}
              {oField('City', 'city')}
              {oField('State', 'state')}
              {oField('ZIP', 'zip')}
              {oField('Timezone', 'timezone')}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <button onClick={() => saveOrg('org')} disabled={savingOrg} style={{ ...btnPrimary, opacity: savingOrg ? 0.5 : 1 }}>
                {savingOrg ? 'Saving...' : 'Save Brokerage'}
              </button>
              {msgOrg && <span style={{ fontSize: 13, color: msgOrg === 'Saved!' ? theme.colors.success : theme.colors.danger }}>{msgOrg}</span>}
            </div>
          </div>

          {/* NJAR Document Templates */}
          <TemplateManager />

          <div style={card}>
            <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 16 }}>Branding & White-Label</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {oField('Logo URL', 'logo_url', 'url')}
              <div>
                <label style={label}>Brand Color</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="color" value={org.brand_color || '#1B5E54'} style={{ width: 40, height: 38, borderRadius: 6, border: `1px solid ${theme.colors.border}`, cursor: 'pointer' }}
                    onChange={e => setOrg(o => ({ ...o, brand_color: e.target.value }))} />
                  <input type="text" value={org.brand_color || '#1B5E54'} style={{ ...input, flex: 1 }}
                    onChange={e => setOrg(o => ({ ...o, brand_color: e.target.value }))} />
                </div>
              </div>
              {oField('Custom Domain', 'custom_domain')}
              {oField('Subdomain', 'subdomain')}
            </div>
            <button onClick={() => saveOrg('branding')} disabled={savingOrg} style={{ ...btnPrimary, marginTop: 16, opacity: savingOrg ? 0.5 : 1 }}>
              Save Branding
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TEAM TAB
// ══════════════════════════════════════════════════════════════

const EMPTY_MEMBER = {
  full_name: '', email: '', phone: '', role: 'agent',
  employment_type: '1099', // '1099' (agent) or 'w2'
  title: '', start_date: '',
  // Agent (1099) fields
  license_number: '', license_expiry: '', license_type: 'salesperson',
  commission_split: '', commission_cap: '',
  mls_id: '', board_membership: '', nrds_id: '',
  eo_insurance_carrier: '', eo_policy_number: '', eo_expiry: '',
  ica_signed_date: '', specialties: [],
  // W2 fields
  salary: '', pay_frequency: 'biweekly', w9_on_file: false,
  // Common
  emergency_contact_name: '', emergency_contact_phone: '',
};

const SPECIALTY_OPTIONS = [
  'Residential', 'Commercial', 'Property Management', 'Luxury',
  'New Construction', 'Investment', 'Land', 'Multi-Family',
];

const LICENSE_TYPES = [
  { value: 'salesperson', label: 'Salesperson' },
  { value: 'broker', label: 'Broker' },
  { value: 'broker_associate', label: 'Broker Associate' },
  { value: 'referral_only', label: 'Referral Only' },
];

function TeamTab() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_MEMBER });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  // Fetch team members
  useEffect(() => {
    (async () => {
      const h = await getHeaders();
      if (!h) { setLoading(false); return; }
      try {
        const r = await fetch('/api/team', { headers: h });
        const d = await r.json();
        setMembers(d.members || []);
      } catch { /* noop */ }
      setLoading(false);
    })();
  }, []);

  const openAddForm = () => {
    setForm({ ...EMPTY_MEMBER });
    setEditingId(null);
    setShowForm(true);
    setMsg('');
  };

  const openEditForm = (member) => {
    setForm({
      ...EMPTY_MEMBER,
      ...member,
      specialties: member.specialties || [],
      commission_split: member.commission_split || '',
      commission_cap: member.commission_cap || '',
      salary: member.salary || '',
      start_date: member.start_date ? member.start_date.slice(0, 10) : '',
      license_expiry: member.license_expiry ? member.license_expiry.slice(0, 10) : '',
      eo_expiry: member.eo_expiry ? member.eo_expiry.slice(0, 10) : '',
      ica_signed_date: member.ica_signed_date ? member.ica_signed_date.slice(0, 10) : '',
    });
    setEditingId(member.id);
    setShowForm(true);
    setMsg('');
  };

  const updateField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const toggleSpecialty = (spec) => {
    setForm(f => ({
      ...f,
      specialties: f.specialties.includes(spec)
        ? f.specialties.filter(s => s !== spec)
        : [...f.specialties, spec],
    }));
  };

  const handleSave = async () => {
    if (!form.full_name || !form.email) {
      setMsg('Name and email are required'); setMsgType('error'); return;
    }

    setSaving(true); setMsg('');
    const h = await getHeaders();

    const payload = { ...form };
    // Clean up numeric fields
    if (payload.commission_split) payload.commission_split = parseFloat(payload.commission_split) || null;
    if (payload.commission_cap) payload.commission_cap = parseFloat(payload.commission_cap) || null;
    if (payload.salary) payload.salary = parseFloat(payload.salary) || null;
    // Clear irrelevant fields based on employment type
    if (payload.employment_type === '1099') {
      payload.salary = null; payload.pay_frequency = null; payload.w9_on_file = false;
    } else {
      payload.license_number = null; payload.license_expiry = null; payload.license_type = null;
      payload.commission_split = null; payload.commission_cap = null;
      payload.mls_id = null; payload.board_membership = null; payload.nrds_id = null;
      payload.eo_insurance_carrier = null; payload.eo_policy_number = null; payload.eo_expiry = null;
      payload.ica_signed_date = null; payload.specialties = [];
    }

    try {
      const action = editingId ? 'update' : 'create';
      const resp = await fetch(`/api/team?action=${action}`, {
        method: 'POST', headers: h,
        body: JSON.stringify(editingId ? { id: editingId, ...payload } : payload),
      });
      const data = await resp.json();

      if (resp.ok) {
        if (editingId) {
          setMembers(prev => prev.map(m => m.id === editingId ? { ...m, ...data.member } : m));
        } else {
          setMembers(prev => [data.member, ...prev]);
        }
        setMsg(editingId ? 'Team member updated' : 'Team member added successfully');
        setMsgType('success');
        setShowForm(false);
        setEditingId(null);
      } else {
        setMsg(data.error || 'Failed to save'); setMsgType('error');
      }
    } catch (e) {
      setMsg('Network error — try again'); setMsgType('error');
    }
    setSaving(false);
  };

  const handleDeactivate = async (id) => {
    const h = await getHeaders();
    try {
      await fetch('/api/team?action=deactivate', {
        method: 'POST', headers: h, body: JSON.stringify({ id }),
      });
      setMembers(prev => prev.map(m => m.id === id ? { ...m, is_active: false, deactivated_at: new Date().toISOString() } : m));
    } catch { /* noop */ }
  };

  const handleReactivate = async (id) => {
    const h = await getHeaders();
    try {
      await fetch('/api/team?action=reactivate', {
        method: 'POST', headers: h, body: JSON.stringify({ id }),
      });
      setMembers(prev => prev.map(m => m.id === id ? { ...m, is_active: true, deactivated_at: null } : m));
    } catch { /* noop */ }
  };

  // Helpers
  const roleBadge = (role) => {
    const map = {
      broker: { bg: `${theme.colors.primary}15`, color: theme.colors.primary, label: 'Broker' },
      admin: { bg: `${theme.colors.info}15`, color: theme.colors.info, label: 'Admin' },
      agent: { bg: `${theme.colors.accent}15`, color: theme.colors.accent, label: 'Agent' },
      pm_manager: { bg: `${theme.colors.warning}15`, color: theme.colors.warning, label: 'PM Manager' },
    };
    const r = map[role] || { bg: `${theme.colors.textLight}15`, color: theme.colors.textLight, label: role };
    return (
      <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: r.bg, color: r.color }}>
        {r.label}
      </span>
    );
  };

  const typeBadge = (type) => (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
      background: type === '1099' ? `${theme.colors.primary}12` : `${theme.colors.info}12`,
      color: type === '1099' ? theme.colors.primary : theme.colors.info,
      letterSpacing: '0.02em',
    }}>
      {type === '1099' ? '1099 Agent' : 'W-2 Employee'}
    </span>
  );

  const isExpiring = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    const diff = (d - now) / (1000 * 60 * 60 * 24);
    return diff <= 60 && diff > 0;
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  if (loading) return <div style={{ color: theme.colors.textLight, padding: 32 }}>Loading team...</div>;

  // ── Add / Edit Form ──
  if (showForm) {
    const isAgent = form.employment_type === '1099';
    const isEdit = !!editingId;

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button onClick={() => { setShowForm(false); setMsg(''); }} style={{ ...btnSecondary, padding: '6px 14px', fontSize: 13 }}>
            &larr; Back
          </button>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text, margin: 0 }}>
            {isEdit ? 'Edit Team Member' : 'Add Team Member'}
          </h2>
        </div>

        {/* Member Type Toggle */}
        <div style={card}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, marginBottom: 12 }}>Member Type</h3>
          <div style={{ display: 'flex', gap: 0, borderRadius: theme.radius.sm, overflow: 'hidden', border: `1px solid ${theme.colors.border}`, width: 'fit-content' }}>
            {[{ value: '1099', label: 'Agent (1099)' }, { value: 'w2', label: 'W-2 Employee' }].map(opt => (
              <button
                key={opt.value}
                onClick={() => {
                  updateField('employment_type', opt.value);
                  // Auto-set role based on type
                  if (opt.value === '1099') updateField('role', 'agent');
                  else if (form.role === 'agent') updateField('role', 'admin');
                }}
                style={{
                  padding: '10px 24px', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
                  background: form.employment_type === opt.value ? theme.colors.primary : theme.colors.card,
                  color: form.employment_type === opt.value ? '#fff' : theme.colors.textLight,
                  transition: 'all 0.15s',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 8 }}>
            {isAgent
              ? 'Independent contractor agent — licensed, commission-based, responsible for own taxes.'
              : 'W-2 salaried employee — admin staff, transaction coordinator, marketing, etc.'}
          </p>
        </div>

        {/* Basic Info */}
        <div style={card}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, marginBottom: 12 }}>Basic Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={label}>Full Name *</label>
              <input type="text" value={form.full_name} style={input} placeholder="Jane Smith"
                onChange={e => updateField('full_name', e.target.value)} />
            </div>
            <div>
              <label style={label}>Email *</label>
              <input type="email" value={form.email} style={input} placeholder="jane@brokerage.com"
                onChange={e => updateField('email', e.target.value)} disabled={isEdit} />
              {!isEdit && <span style={{ fontSize: 11, color: theme.colors.textLight }}>An invite will be sent — they create their own password</span>}
            </div>
            <div>
              <label style={label}>Phone</label>
              <input type="tel" value={form.phone} style={input} placeholder="(555) 123-4567"
                onChange={e => updateField('phone', e.target.value)} />
            </div>
            {/* Role: 1099 is always Agent, W2 shows admin/TC/PM/viewer */}
            {isAgent ? (
              <div>
                <label style={label}>Role</label>
                <input type="text" value="Agent" style={{ ...input, background: theme.colors.bg, color: theme.colors.textLight }} disabled />
                <span style={{ fontSize: 11, color: theme.colors.textLight }}>1099 contractors are always Agents</span>
              </div>
            ) : (
              <div>
                <label style={label}>Role *</label>
                <select value={form.role} onChange={e => updateField('role', e.target.value)} style={{ ...input, cursor: 'pointer' }}>
                  <option value="admin">Admin</option>
                  <option value="pm_manager">Property Manager</option>
                  <option value="viewer">Viewer (Read-only)</option>
                </select>
              </div>
            )}
            <div>
              <label style={label}>{isAgent ? 'Title / Designation' : 'Job Title *'}</label>
              <input type="text" value={form.title} style={input} placeholder={isAgent ? 'e.g. Realtor, Broker Associate' : 'e.g. Transaction Coordinator'}
                onChange={e => updateField('title', e.target.value)} />
            </div>
            <div>
              <label style={label}>Start Date</label>
              <input type="date" value={form.start_date} style={input}
                onChange={e => updateField('start_date', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Agent-specific: Upload Documents FIRST */}
        {isAgent && (
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, marginBottom: 4 }}>Upload Documents</h3>
            <p style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 16 }}>
              Upload the agent's license and E&O certificate. Trovva will automatically read and fill in the details below.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 8 }}>
              {/* License Upload */}
              <div>
                <label style={{ ...label, marginBottom: 8 }}>NJ Real Estate License</label>
                <label style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: '24px 16px', border: `2px dashed ${form._license_file ? theme.colors.success : theme.colors.border}`,
                  borderRadius: theme.radius.md, cursor: 'pointer', fontSize: 13, fontWeight: 500,
                  background: form._license_file ? `${theme.colors.success}06` : theme.colors.bg,
                  color: form._license_file ? theme.colors.success : theme.colors.textLight,
                  transition: 'all 0.15s', minHeight: 100,
                }}>
                  {form._license_file ? (
                    <>
                      <span style={{ fontSize: 24 }}>&#10003;</span>
                      <span>{form._license_file.name}</span>
                      <button onClick={(e) => { e.preventDefault(); updateField('_license_file', null); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.danger, fontSize: 11, textDecoration: 'underline' }}>Remove</button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 28, opacity: 0.5 }}>&#128196;</span>
                      <span>Drop license here or click to upload</span>
                      <span style={{ fontSize: 11, opacity: 0.7 }}>PDF, JPG, or PNG</span>
                    </>
                  )}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                    onChange={e => { const file = e.target.files?.[0]; if (file) updateField('_license_file', file); }} />
                </label>
              </div>

              {/* E&O Upload */}
              <div>
                <label style={{ ...label, marginBottom: 8 }}>E&O Insurance Certificate</label>
                <label style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: '24px 16px', border: `2px dashed ${form._eo_file ? theme.colors.success : theme.colors.border}`,
                  borderRadius: theme.radius.md, cursor: 'pointer', fontSize: 13, fontWeight: 500,
                  background: form._eo_file ? `${theme.colors.success}06` : theme.colors.bg,
                  color: form._eo_file ? theme.colors.success : theme.colors.textLight,
                  transition: 'all 0.15s', minHeight: 100,
                }}>
                  {form._eo_file ? (
                    <>
                      <span style={{ fontSize: 24 }}>&#10003;</span>
                      <span>{form._eo_file.name}</span>
                      <button onClick={(e) => { e.preventDefault(); updateField('_eo_file', null); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.danger, fontSize: 11, textDecoration: 'underline' }}>Remove</button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 28, opacity: 0.5 }}>&#128203;</span>
                      <span>Drop E&O cert here or click to upload</span>
                      <span style={{ fontSize: 11, opacity: 0.7 }}>Required for all NJ agents</span>
                    </>
                  )}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                    onChange={e => { const file = e.target.files?.[0]; if (file) updateField('_eo_file', file); }} />
                </label>
              </div>
            </div>

            {(form._license_file || form._eo_file) && (
              <div style={{
                padding: '8px 14px', borderRadius: theme.radius.sm,
                background: `${theme.colors.info}08`, border: `1px solid ${theme.colors.info}20`,
                fontSize: 12, color: theme.colors.info, marginTop: 8,
              }}>
                Trovva AI will extract license number, expiry, carrier, and policy details from the uploaded documents after saving.
              </div>
            )}
          </div>
        )}

        {/* Agent-specific: License & Compliance details (auto-filled by doc upload or manual) */}
        {isAgent && (
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, margin: 0 }}>License & Compliance Details</h3>
              <span style={{ fontSize: 11, color: theme.colors.textLight, fontStyle: 'italic' }}>Auto-filled from documents or enter manually</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={label}>NJ License Number</label>
                <input type="text" value={form.license_number} style={input} placeholder="e.g. 1234567"
                  onChange={e => updateField('license_number', e.target.value)} />
              </div>
              <div>
                <label style={label}>License Type</label>
                <select value={form.license_type} onChange={e => updateField('license_type', e.target.value)} style={{ ...input, cursor: 'pointer' }}>
                  {LICENSE_TYPES.map(lt => <option key={lt.value} value={lt.value}>{lt.label}</option>)}
                </select>
              </div>
              <div>
                <label style={label}>License Expiry</label>
                <input type="date" value={form.license_expiry} style={input}
                  onChange={e => updateField('license_expiry', e.target.value)} />
              </div>
              <div>
                <label style={label}>ICA Signed Date</label>
                <input type="date" value={form.ica_signed_date} style={input}
                  onChange={e => updateField('ica_signed_date', e.target.value)} />
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text, marginBottom: 8 }}>MLS & Board</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                <div>
                  <label style={label}>Bright MLS ID</label>
                  <input type="text" value={form.mls_id} style={input} placeholder="MLS Agent ID"
                    onChange={e => updateField('mls_id', e.target.value)} />
                </div>
                <div>
                  <label style={label}>Board Membership</label>
                  <input type="text" value={form.board_membership} style={input} placeholder="e.g. GSAR, NJAR"
                    onChange={e => updateField('board_membership', e.target.value)} />
                </div>
                <div>
                  <label style={label}>NRDS ID</label>
                  <input type="text" value={form.nrds_id} style={input} placeholder="NAR Member ID"
                    onChange={e => updateField('nrds_id', e.target.value)} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Agent-specific: E&O Insurance details */}
        {isAgent && (
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, margin: 0 }}>E&O Insurance Details</h3>
              <span style={{ fontSize: 11, color: theme.colors.textLight, fontStyle: 'italic' }}>Auto-filled from certificate or enter manually</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <div>
                <label style={label}>Insurance Carrier</label>
                <input type="text" value={form.eo_insurance_carrier} style={input} placeholder="e.g. CRES, Pearl"
                  onChange={e => updateField('eo_insurance_carrier', e.target.value)} />
              </div>
              <div>
                <label style={label}>Policy Number</label>
                <input type="text" value={form.eo_policy_number} style={input}
                  onChange={e => updateField('eo_policy_number', e.target.value)} />
              </div>
              <div>
                <label style={label}>E&O Expiry Date</label>
                <input type="date" value={form.eo_expiry} style={input}
                  onChange={e => updateField('eo_expiry', e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {/* Agent-specific: Commission */}
        {isAgent && (
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, marginBottom: 12 }}>Commission Structure</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={label}>Agent Split (%)</label>
                <input type="number" value={form.commission_split} style={input} placeholder="e.g. 70" min={0} max={100} step={0.5}
                  onChange={e => updateField('commission_split', e.target.value)} />
                <span style={{ fontSize: 11, color: theme.colors.textLight }}>Agent keeps this %, brokerage keeps the rest</span>
              </div>
              <div>
                <label style={label}>Annual Cap ($)</label>
                <input type="number" value={form.commission_cap} style={input} placeholder="e.g. 25000 (blank = no cap)" min={0} step={500}
                  onChange={e => updateField('commission_cap', e.target.value)} />
                <span style={{ fontSize: 11, color: theme.colors.textLight }}>Agent goes to 100% after paying this amount</span>
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              <label style={label}>Specialties</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {SPECIALTY_OPTIONS.map(spec => {
                  const active = (form.specialties || []).includes(spec.toLowerCase());
                  return (
                    <button key={spec} onClick={() => toggleSpecialty(spec.toLowerCase())}
                      style={{
                        padding: '5px 12px', borderRadius: 16, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                        border: `1px solid ${active ? theme.colors.primary : theme.colors.border}`,
                        background: active ? `${theme.colors.primary}12` : theme.colors.card,
                        color: active ? theme.colors.primary : theme.colors.textLight,
                        transition: 'all 0.15s',
                      }}>
                      {spec}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* W2-specific: Compensation */}
        {!isAgent && (
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, marginBottom: 12 }}>Compensation</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <div>
                <label style={label}>Annual Salary ($)</label>
                <input type="number" value={form.salary} style={input} placeholder="e.g. 55000" min={0} step={1000}
                  onChange={e => updateField('salary', e.target.value)} />
              </div>
              <div>
                <label style={label}>Pay Frequency</label>
                <select value={form.pay_frequency} onChange={e => updateField('pay_frequency', e.target.value)} style={{ ...input, cursor: 'pointer' }}>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.w9_on_file} onChange={e => updateField('w9_on_file', e.target.checked)} />
                  <span style={{ fontSize: 13, color: theme.colors.text, fontWeight: 500 }}>W-9 on file</span>
                </label>
              </div>
            </div>
            {/* W-9 upload */}
            <div style={{ marginTop: 14 }}>
              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.sm,
                cursor: 'pointer', color: theme.colors.textLight, fontSize: 12, fontWeight: 500,
                background: theme.colors.card,
              }}>
                <span>+ Upload W-9 Form</span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) updateField('_w9_file', file);
                  }} />
              </label>
              {form._w9_file && (
                <span style={{ marginLeft: 8, fontSize: 12, color: theme.colors.success }}>
                  {form._w9_file.name}
                  <button onClick={() => updateField('_w9_file', null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.danger, fontSize: 12, marginLeft: 4 }}>x</button>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Emergency Contact */}
        <div style={card}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, marginBottom: 12 }}>Emergency Contact</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={label}>Contact Name</label>
              <input type="text" value={form.emergency_contact_name} style={input} placeholder="Emergency contact full name"
                onChange={e => updateField('emergency_contact_name', e.target.value)} />
            </div>
            <div>
              <label style={label}>Contact Phone</label>
              <input type="tel" value={form.emergency_contact_phone} style={input} placeholder="(555) 123-4567"
                onChange={e => updateField('emergency_contact_phone', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
          <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, padding: '10px 28px', fontSize: 15, opacity: saving ? 0.5 : 1 }}>
            {saving ? 'Saving...' : isEdit ? 'Update Member' : 'Add Team Member'}
          </button>
          <button onClick={() => { setShowForm(false); setMsg(''); }} style={btnSecondary}>Cancel</button>
          {msg && <span style={{ fontSize: 13, color: msgType === 'success' ? theme.colors.success : theme.colors.danger }}>{msg}</span>}
        </div>
      </div>
    );
  }

  // ── Roster View ──
  const activeMembers = members.filter(m => m.is_active !== false);
  const inactiveMembers = members.filter(m => m.is_active === false);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text, margin: 0 }}>Team Members</h2>
          <p style={{ fontSize: 13, color: theme.colors.textLight, margin: '4px 0 0' }}>
            {activeMembers.length} active member{activeMembers.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button onClick={openAddForm} style={{ ...btnPrimary, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Add Team Member
        </button>
      </div>

      {msg && (
        <div style={{
          padding: '10px 16px', borderRadius: theme.radius.sm, marginBottom: 16, fontSize: 13,
          background: msgType === 'success' ? `${theme.colors.success}12` : `${theme.colors.danger}12`,
          color: msgType === 'success' ? theme.colors.success : theme.colors.danger, fontWeight: 500,
        }}>
          {msg}
        </div>
      )}

      {/* Active Members */}
      {activeMembers.length === 0 && (
        <div style={{ ...card, textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
          <h3 style={{ color: theme.colors.text, marginBottom: 4 }}>No team members yet</h3>
          <p style={{ color: theme.colors.textLight, fontSize: 13, marginBottom: 16 }}>
            Add your first agent or employee to get started.
          </p>
          <button onClick={openAddForm} style={btnPrimary}>Add Team Member</button>
        </div>
      )}

      {activeMembers.map(member => {
        const expanded = expandedId === member.id;
        const isAgent1099 = member.employment_type === '1099';
        const licExpiring = isExpiring(member.license_expiry);
        const licExpired = isExpired(member.license_expiry);
        const eoExpiring = isExpiring(member.eo_expiry);
        const eoExpired = isExpired(member.eo_expiry);

        return (
          <div key={member.id} style={{ ...card, padding: 0, marginBottom: 10, overflow: 'hidden' }}>
            {/* Summary row */}
            <div
              onClick={() => setExpandedId(expanded ? null : member.id)}
              style={{
                display: 'flex', alignItems: 'center', padding: '14px 18px', cursor: 'pointer',
                gap: 14, transition: 'background 0.1s',
              }}
            >
              {/* Avatar */}
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.colors.primary}30, ${theme.colors.accent}30)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, fontWeight: 700, color: theme.colors.primary, flexShrink: 0,
              }}>
                {member.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
              </div>

              {/* Name & info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>{member.full_name}</span>
                  {roleBadge(member.role)}
                  {typeBadge(member.employment_type || '1099')}
                  {(licExpired || eoExpired) && (
                    <span style={{ fontSize: 10, fontWeight: 600, color: theme.colors.danger, background: `${theme.colors.danger}12`, padding: '2px 6px', borderRadius: 8 }}>
                      EXPIRED
                    </span>
                  )}
                  {!licExpired && !eoExpired && (licExpiring || eoExpiring) && (
                    <span style={{ fontSize: 10, fontWeight: 600, color: theme.colors.warning, background: `${theme.colors.warning}12`, padding: '2px 6px', borderRadius: 8 }}>
                      EXPIRING SOON
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 2 }}>
                  {member.email}
                  {member.phone && <span> &middot; {member.phone}</span>}
                  {member.title && <span> &middot; {member.title}</span>}
                </div>
              </div>

              {/* Expand arrow */}
              <span style={{ color: theme.colors.textLight, fontSize: 12, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>
                &#9660;
              </span>
            </div>

            {/* Expanded details */}
            {expanded && (
              <div style={{ padding: '0 18px 16px', borderTop: `1px solid ${theme.colors.border}` }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, padding: '16px 0' }}>
                  {isAgent1099 && member.license_number && (
                    <div>
                      <div style={{ ...label, marginBottom: 2 }}>License</div>
                      <div style={{ fontSize: 14, color: theme.colors.text, fontWeight: 500 }}>
                        {member.license_number}
                        {member.license_type && <span style={{ fontSize: 12, color: theme.colors.textLight }}> ({member.license_type})</span>}
                      </div>
                      {member.license_expiry && (
                        <div style={{ fontSize: 12, color: licExpired ? theme.colors.danger : licExpiring ? theme.colors.warning : theme.colors.textLight, fontWeight: licExpired || licExpiring ? 600 : 400 }}>
                          Exp: {formatDate(member.license_expiry)}
                        </div>
                      )}
                    </div>
                  )}
                  {isAgent1099 && member.commission_split != null && (
                    <div>
                      <div style={{ ...label, marginBottom: 2 }}>Commission Split</div>
                      <div style={{ fontSize: 14, color: theme.colors.text, fontWeight: 500 }}>{member.commission_split}% agent</div>
                      {member.commission_cap && <div style={{ fontSize: 12, color: theme.colors.textLight }}>Cap: ${Number(member.commission_cap).toLocaleString()}</div>}
                    </div>
                  )}
                  {isAgent1099 && member.mls_id && (
                    <div>
                      <div style={{ ...label, marginBottom: 2 }}>MLS / Board</div>
                      <div style={{ fontSize: 14, color: theme.colors.text }}>{member.mls_id}</div>
                      {member.board_membership && <div style={{ fontSize: 12, color: theme.colors.textLight }}>{member.board_membership}</div>}
                    </div>
                  )}
                  {isAgent1099 && member.eo_insurance_carrier && (
                    <div>
                      <div style={{ ...label, marginBottom: 2 }}>E&O Insurance</div>
                      <div style={{ fontSize: 14, color: theme.colors.text }}>{member.eo_insurance_carrier}</div>
                      {member.eo_expiry && (
                        <div style={{ fontSize: 12, color: eoExpired ? theme.colors.danger : eoExpiring ? theme.colors.warning : theme.colors.textLight, fontWeight: eoExpired || eoExpiring ? 600 : 400 }}>
                          Exp: {formatDate(member.eo_expiry)}
                        </div>
                      )}
                    </div>
                  )}
                  {!isAgent1099 && member.salary && (
                    <div>
                      <div style={{ ...label, marginBottom: 2 }}>Salary</div>
                      <div style={{ fontSize: 14, color: theme.colors.text, fontWeight: 500 }}>${Number(member.salary).toLocaleString()}/yr</div>
                      {member.pay_frequency && <div style={{ fontSize: 12, color: theme.colors.textLight }}>Paid {member.pay_frequency}</div>}
                    </div>
                  )}
                  {!isAgent1099 && (
                    <div>
                      <div style={{ ...label, marginBottom: 2 }}>W-9 Status</div>
                      <div style={{ fontSize: 14, color: member.w9_on_file ? theme.colors.success : theme.colors.warning, fontWeight: 500 }}>
                        {member.w9_on_file ? 'On file' : 'Missing'}
                      </div>
                    </div>
                  )}
                  {member.start_date && (
                    <div>
                      <div style={{ ...label, marginBottom: 2 }}>Start Date</div>
                      <div style={{ fontSize: 14, color: theme.colors.text }}>{formatDate(member.start_date)}</div>
                    </div>
                  )}
                  {member.emergency_contact_name && (
                    <div>
                      <div style={{ ...label, marginBottom: 2 }}>Emergency Contact</div>
                      <div style={{ fontSize: 14, color: theme.colors.text }}>{member.emergency_contact_name}</div>
                      {member.emergency_contact_phone && <div style={{ fontSize: 12, color: theme.colors.textLight }}>{member.emergency_contact_phone}</div>}
                    </div>
                  )}
                  {isAgent1099 && member.specialties?.length > 0 && (
                    <div>
                      <div style={{ ...label, marginBottom: 2 }}>Specialties</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {member.specialties.map(s => (
                          <span key={s} style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, background: `${theme.colors.primary}10`, color: theme.colors.primary, fontWeight: 500, textTransform: 'capitalize' }}>{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, paddingTop: 8, borderTop: `1px solid ${theme.colors.border}` }}>
                  <button onClick={() => openEditForm(member)} style={{ ...btnSecondary, fontSize: 12, padding: '6px 14px' }}>Edit</button>
                  <button onClick={() => handleDeactivate(member.id)} style={{ ...btnSecondary, fontSize: 12, padding: '6px 14px', color: theme.colors.danger, borderColor: `${theme.colors.danger}40` }}>
                    Deactivate
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Inactive Members */}
      {inactiveMembers.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: theme.colors.textLight, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Inactive ({inactiveMembers.length})
          </h3>
          {inactiveMembers.map(member => (
            <div key={member.id} style={{ ...card, padding: '12px 18px', marginBottom: 6, opacity: 0.7, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 500, color: theme.colors.text }}>{member.full_name}</span>
                <span style={{ marginLeft: 8, fontSize: 12, color: theme.colors.textLight }}>{member.email}</span>
                {member.deactivated_at && <span style={{ marginLeft: 8, fontSize: 11, color: theme.colors.textLight }}>Deactivated {formatDate(member.deactivated_at)}</span>}
              </div>
              <button onClick={() => handleReactivate(member.id)} style={{ ...btnSecondary, fontSize: 12, padding: '4px 12px' }}>Reactivate</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// BILLING TAB
// ══════════════════════════════════════════════════════════════
function BillingTab() {
  const [status, setStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [seats, setSeats] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    (async () => {
      const h = await getHeaders();
      if (!h) { setLoading(false); return; }
      const [s, i] = await Promise.all([
        fetch('/api/stripe-billing?action=status', { headers: h }).then(r => r.json()),
        fetch('/api/stripe-billing?action=invoices', { headers: h }).then(r => r.json()).catch(() => ({ invoices: [] })),
      ]);
      setStatus(s); setInvoices(i.invoices || []); setSeats(s.agent_seats || 1); setLoading(false);
    })();
  }, []);

  const subscribe = async () => {
    const h = await getHeaders();
    const resp = await fetch('/api/stripe-billing?action=create_checkout', { method: 'POST', headers: h, body: JSON.stringify({ agent_seats: seats }) });
    const data = await resp.json();
    if (data.url) window.location.href = data.url;
  };

  const openPortal = async () => {
    const h = await getHeaders();
    const resp = await fetch('/api/stripe-billing?action=portal', { method: 'POST', headers: h });
    const data = await resp.json();
    if (data.url) window.location.href = data.url;
  };

  const buyPack = async (pack) => {
    const h = await getHeaders();
    const resp = await fetch('/api/stripe-billing?action=buy_envelopes', { method: 'POST', headers: h, body: JSON.stringify({ pack }) });
    const data = await resp.json();
    if (data.url) window.location.href = data.url;
  };

  const updateSeats = async () => {
    const h = await getHeaders();
    const resp = await fetch('/api/stripe-billing?action=update_seats', { method: 'POST', headers: h, body: JSON.stringify({ agent_seats: seats }) });
    if (resp.ok) {
      const data = await resp.json();
      setStatus(s => ({ ...s, agent_seats: data.agent_seats, tier: data.tier, per_seat: data.per_seat, monthly_total: data.monthly_cost }));
      setShowUpgrade(false);
    }
  };

  if (loading) return <div style={{ color: theme.colors.textLight, padding: 32 }}>Loading billing...</div>;

  const envelopePct = status?.envelopes ? Math.min(100, Math.round((status.envelopes.used / status.envelopes.cap) * 100)) : 0;
  const tierName = (s) => s <= 1 ? 'Trovva One' : s <= 5 ? 'Trovva Team' : 'Trovva Pro';
  const tierPrice = (s) => s <= 1 ? 149 : s <= 5 ? 129 : 99;
  const tierCost = (s) => s <= 1 ? 149 : s <= 5 ? s * 129 : s * 99;
  const isActive = status?.plan === 'active';
  const currentTier = isActive ? (status.tier || tierName(status.agent_seats || 1)) : null;
  const currentSeats = status?.agent_seats || 1;

  const PLANS = [
    { id: 'one', name: 'Trovva One', seats: '1 seat', price: 149, perSeat: 149, desc: 'Solo broker or single agent', seatRange: [1, 1] },
    { id: 'team', name: 'Trovva Team', seats: '2–5 seats', price: 129, perSeat: 129, desc: 'Small brokerage team', seatRange: [2, 5] },
    { id: 'pro', name: 'Trovva Pro', seats: '6+ seats', price: 99, perSeat: 99, desc: 'Growing brokerage', seatRange: [6, 50] },
  ];

  const currentPlanId = currentSeats <= 1 ? 'one' : currentSeats <= 5 ? 'team' : 'pro';

  return (
    <div>
      {/* Current plan summary */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 8 }}>Current Plan</h2>
            {isActive ? (
              <>
                <div style={{ fontSize: 22, fontWeight: 700, color: theme.colors.text, marginBottom: 4 }}>
                  {currentTier}
                </div>
                <div style={{ fontSize: 14, color: theme.colors.textLight, marginBottom: 2 }}>
                  {currentSeats} seat{currentSeats > 1 ? 's' : ''} &middot; ${tierPrice(currentSeats)}/seat/mo &middot; <strong style={{ color: theme.colors.text }}>${tierCost(currentSeats)}/mo</strong>
                </div>
                {status.period_end && (
                  <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 4 }}>
                    Next billing date: {formatDateShort(status.period_end, true)}
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ fontSize: 22, fontWeight: 700, color: theme.colors.text, marginBottom: 4 }}>
                  Free Trial
                </div>
                <div style={{ fontSize: 14, color: theme.colors.textLight }}>
                  You're on the 14-day free trial. Subscribe to keep your data and unlock all features.
                </div>
              </>
            )}
          </div>
          <span style={{
            padding: '5px 14px', borderRadius: 14, fontSize: 12, fontWeight: 600,
            background: isActive ? `${theme.colors.success}14` : `${theme.colors.warning}14`,
            color: isActive ? theme.colors.success : theme.colors.warning,
            flexShrink: 0,
          }}>
            {isActive ? 'Subscribed' : 'Trial'}
          </span>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          {isActive && (
            <button onClick={openPortal} style={btnSecondary}>Manage Subscription</button>
          )}
          <button
            onClick={() => setShowUpgrade(!showUpgrade)}
            style={showUpgrade ? btnSecondary : { ...btnPrimary }}
          >
            {showUpgrade ? 'Cancel' : isActive ? 'Change Plan' : 'Subscribe'}
          </button>
        </div>
      </div>

      {/* Upgrade / Plan picker — only shown when button pressed */}
      {showUpgrade && (
        <div style={card}>
          <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 16 }}>
            {isActive ? 'Change Your Plan' : 'Choose a Plan'}
          </h2>

          {/* Plan cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
            {PLANS.map(plan => {
              const isCurrent = isActive && plan.id === currentPlanId;
              const isSelected = selectedPlan === plan.id;
              return (
                <button
                  key={plan.id}
                  onClick={() => {
                    setSelectedPlan(plan.id);
                    if (plan.id === 'one') setSeats(1);
                    else if (plan.id === 'team') setSeats(Math.max(2, Math.min(5, seats)));
                    else setSeats(Math.max(6, seats));
                  }}
                  style={{
                    padding: 16, borderRadius: theme.radius.md, cursor: 'pointer', textAlign: 'left',
                    border: `2px solid ${isSelected ? theme.colors.primary : isCurrent ? theme.colors.success : theme.colors.border}`,
                    background: isSelected ? `${theme.colors.primary}06` : theme.colors.card,
                    position: 'relative', transition: 'all 0.15s',
                  }}
                >
                  {isCurrent && (
                    <span style={{
                      position: 'absolute', top: -8, right: 12, fontSize: 10, fontWeight: 600,
                      background: theme.colors.success, color: '#fff', padding: '2px 8px', borderRadius: 8,
                    }}>CURRENT</span>
                  )}
                  <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text, marginBottom: 2 }}>{plan.name}</div>
                  <div style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 8 }}>{plan.desc}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: theme.colors.primary }}>
                    ${plan.price}<span style={{ fontSize: 13, fontWeight: 400, color: theme.colors.textLight }}>/seat/mo</span>
                  </div>
                  <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 2 }}>{plan.seats}</div>
                </button>
              );
            })}
          </div>

          {/* Seat selector — shows for Team and Pro */}
          {selectedPlan && selectedPlan !== 'one' && (
            <div style={{
              padding: 16, borderRadius: theme.radius.md, background: theme.colors.bg,
              border: `1px solid ${theme.colors.border}`, marginBottom: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <label style={{ fontSize: 14, fontWeight: 500, color: theme.colors.text }}>Number of seats:</label>
                <input
                  type="number"
                  min={selectedPlan === 'team' ? 2 : 6}
                  max={selectedPlan === 'team' ? 5 : 50}
                  value={seats}
                  style={{ ...input, width: 80, textAlign: 'center', fontSize: 16, fontWeight: 600 }}
                  onChange={e => {
                    const v = parseInt(e.target.value) || (selectedPlan === 'team' ? 2 : 6);
                    const min = selectedPlan === 'team' ? 2 : 6;
                    const max = selectedPlan === 'team' ? 5 : 50;
                    setSeats(Math.max(min, Math.min(max, v)));
                  }}
                />
                <span style={{ fontSize: 14, color: theme.colors.textLight }}>
                  {tierName(seats)} = <strong style={{ color: theme.colors.text }}>${tierCost(seats)}/mo</strong>
                </span>
              </div>
            </div>
          )}

          {/* One seat summary */}
          {selectedPlan === 'one' && (
            <div style={{
              padding: 16, borderRadius: theme.radius.md, background: theme.colors.bg,
              border: `1px solid ${theme.colors.border}`, marginBottom: 16,
              fontSize: 14, color: theme.colors.textLight,
            }}>
              1 seat &middot; <strong style={{ color: theme.colors.text }}>$149/mo</strong>
            </div>
          )}

          {/* Subscribe / Update button */}
          {selectedPlan && (
            <button
              onClick={isActive ? updateSeats : subscribe}
              style={{ ...btnPrimary, padding: '10px 28px', fontSize: 15 }}
            >
              {isActive ? `Update to ${tierName(seats)} (${seats} seat${seats > 1 ? 's' : ''})` : `Subscribe — $${tierCost(seats)}/mo`}
            </button>
          )}
        </div>
      )}

      {/* Envelope usage */}
      <div style={card}>
        <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 12 }}>E-Signature Envelopes</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
          <div style={{ flex: 1, background: theme.colors.border, borderRadius: 6, height: 8 }}>
            <div style={{ height: 8, borderRadius: 6, width: `${envelopePct}%`, background: envelopePct > 80 ? theme.colors.danger : theme.colors.primary, transition: 'width 0.3s' }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 500, color: theme.colors.text }}>{status?.envelopes?.used || 0} / {status?.envelopes?.cap || 10}</span>
        </div>
        <p style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 12 }}>10 envelopes per seat per month. Need more?</p>
        <div style={{ display: 'flex', gap: 8 }}>
          {[{ pack: '10_pack', label: '10 for $15' }, { pack: '30_pack', label: '30 for $35' }, { pack: '100_pack', label: '100 for $99' }].map(p => (
            <button key={p.pack} onClick={() => buyPack(p.pack)} style={btnSecondary}>{p.label}</button>
          ))}
        </div>
      </div>

      {/* Invoice history */}
      {invoices.length > 0 && (
        <div style={card}>
          <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 12 }}>Invoice History</h2>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.colors.border}`, textAlign: 'left' }}>
                <th style={{ padding: '8px 0', color: theme.colors.textLight, fontWeight: 500 }}>Date</th>
                <th style={{ padding: '8px 0', color: theme.colors.textLight, fontWeight: 500 }}>Amount</th>
                <th style={{ padding: '8px 0', color: theme.colors.textLight, fontWeight: 500 }}>Status</th>
                <th style={{ padding: '8px 0', color: theme.colors.textLight, fontWeight: 500 }}>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                  <td style={{ padding: '8px 0', color: theme.colors.text }}>{formatDate(inv.created_at)}</td>
                  <td style={{ padding: '8px 0', color: theme.colors.text, fontWeight: 500 }}>${(inv.amount_cents / 100).toFixed(2)}</td>
                  <td style={{ padding: '8px 0' }}>
                    <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, background: inv.status === 'paid' ? `${theme.colors.success}18` : `${theme.colors.warning}18`, color: inv.status === 'paid' ? theme.colors.success : theme.colors.warning }}>{inv.status}</span>
                  </td>
                  <td style={{ padding: '8px 0' }}>{inv.invoice_url && <a href={inv.invoice_url} target="_blank" rel="noreferrer" style={{ color: theme.colors.primary, textDecoration: 'none' }}>View</a>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// INTEGRATIONS TAB
// ══════════════════════════════════════════════════════════════
function IntegrationsTab() {
  const [integrations, setIntegrations] = useState(null);

  useEffect(() => {
    (async () => {
      const h = await getHeaders();
      if (!h) return;
      const r = await fetch('/api/settings?section=integrations', { headers: h });
      const d = await r.json();
      setIntegrations(d.integrations);
    })();
  }, []);

  if (!integrations) return <div style={{ color: theme.colors.textLight, padding: 32 }}>Loading integrations...</div>;

  const IntCard = ({ name, icon, connected, detail, connectUrl }) => (
    <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 16, padding: 16, marginBottom: 8 }}>
      <div style={{ fontSize: 24 }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: theme.colors.text }}>{name}</div>
        <div style={{ fontSize: 12, color: theme.colors.textLight }}>{detail}</div>
      </div>
      {connected ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 500, color: theme.colors.success }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: theme.colors.success }} /> Connected
        </span>
      ) : connectUrl ? (
        <a href={connectUrl} style={{ ...btnSecondary, textDecoration: 'none', fontSize: 12, padding: '6px 14px', color: theme.colors.primary, borderColor: `${theme.colors.primary}40` }}>Connect</a>
      ) : (
        <span style={{ fontSize: 12, color: theme.colors.textLight }}>Not configured</span>
      )}
    </div>
  );

  return (
    <div>
      <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 12 }}>Website Tracking</h2>
      <PixelSetup />

      <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 12, marginTop: 28 }}>Connected Integrations</h2>
      <IntCard name="Google (Gmail + Calendar)" icon="📧" connected={integrations.google?.connected} detail={integrations.google?.email || 'Connect your Google account'} connectUrl="/api/google-auth" />
      <IntCard name="Microsoft 365 (Outlook)" icon="📬" connected={integrations.microsoft?.connected} detail={integrations.microsoft?.email || 'Connect Outlook email & calendar'} connectUrl="/api/microsoft-auth" />
      <SmsProvisioningCard />
      <IntCard name="SendGrid (Email)" icon="✉️" connected={integrations.sendgrid?.configured} detail={integrations.sendgrid?.configured ? 'API key configured' : 'Add SENDGRID_API_KEY in env'} />
      <IntCard name="BoldSign (E-Signature)" icon="✍️" connected={integrations.boldsign?.configured} detail={integrations.boldsign?.configured ? 'Ready for e-signatures' : 'Add BOLDSIGN_API_KEY in env'} />
      <IntCard name="Facebook Lead Ads" icon="📘" connected={integrations.facebook?.configured} detail={integrations.facebook?.configured ? 'Webhook active' : 'Set up Facebook webhook'} />
      <IntCard name="Zillow Tech Connect" icon="🏠" connected={integrations.zillow?.configured} detail={integrations.zillow?.configured ? 'Webhook active' : 'Set up Zillow webhook key'} />
      <IntCard name="Zapier" icon="⚡" connected={integrations.zapier?.active_keys > 0} detail={integrations.zapier?.active_keys > 0 ? `${integrations.zapier.active_keys} active API key${integrations.zapier.active_keys > 1 ? 's' : ''}` : 'Create API keys for Zapier'} />

      <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 12, marginTop: 28 }}>Voice & Phone</h2>
      <CallerIdProtection />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// SMS PROVISIONING CARD (inside Integrations tab)
// ══════════════════════════════════════════════════════════════
function SmsProvisioningCard() {
  const [status, setStatus] = useState(null); // { provisioned, phone_number, provisioned_at }
  const [loading, setLoading] = useState(true);
  const [areaCode, setAreaCode] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [purchasing, setPurchasing] = useState(null); // phone number being purchased
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const getHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session ? { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' } : null;
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const h = await getHeaders();
        if (!h) return;
        const r = await fetch('/api/twilio-provision', { headers: h });
        if (r.ok) setStatus(await r.json());
      } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  const searchNumbers = async () => {
    if (!areaCode || areaCode.length !== 3) { setError('Enter a 3-digit NJ area code (e.g., 973, 201, 609, 856)'); return; }
    setSearching(true); setError(null); setSearchResults(null);
    try {
      const h = await getHeaders();
      const r = await fetch('/api/twilio-provision?action=search', {
        method: 'POST', headers: h, body: JSON.stringify({ area_code: areaCode }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error);
      setSearchResults(data.numbers || []);
      if (!data.numbers?.length) setError('No numbers available in this area code. Try another.');
    } catch (e) { setError(e.message); }
    finally { setSearching(false); }
  };

  const purchaseNumber = async (phoneNumber) => {
    if (!confirm(`Purchase ${phoneNumber} for your brokerage? This will be your SMS number for all agent texting. Cost: $1.15/month.`)) return;
    setPurchasing(phoneNumber); setError(null);
    try {
      const h = await getHeaders();
      const r = await fetch('/api/twilio-provision?action=purchase', {
        method: 'POST', headers: h, body: JSON.stringify({ phone_number: phoneNumber }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error);
      setStatus({ provisioned: true, phone_number: phoneNumber, provisioned_at: new Date().toISOString() });
      setSearchResults(null);
      setExpanded(false);
    } catch (e) { setError(e.message); }
    finally { setPurchasing(null); }
  };

  const formatPhone = (n) => {
    if (!n) return '';
    const d = n.replace(/\D/g, '').slice(-10);
    return d.length === 10 ? `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}` : n;
  };

  if (loading) {
    return (
      <div style={{ background: theme.colors.card, borderRadius: 12, border: `1px solid ${theme.colors.border}`, padding: 16, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ fontSize: 24 }}>📱</div>
        <div style={{ flex: 1, color: theme.colors.textLight, fontSize: 13 }}>Loading SMS settings...</div>
      </div>
    );
  }

  return (
    <div style={{ background: theme.colors.card, borderRadius: 12, border: `1px solid ${status?.provisioned ? theme.colors.success + '40' : theme.colors.border}`, marginBottom: 8, overflow: 'hidden' }}>
      {/* Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 16, cursor: 'pointer' }}
      >
        <div style={{ fontSize: 24 }}>📱</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: theme.colors.text }}>SMS & Texting</div>
          <div style={{ fontSize: 12, color: theme.colors.textLight }}>
            {status?.provisioned
              ? `Brokerage number: ${formatPhone(status.phone_number)}`
              : 'Get a dedicated phone number for your brokerage'}
          </div>
        </div>
        {status?.provisioned ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 500, color: theme.colors.success }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: theme.colors.success }} /> Active
          </span>
        ) : (
          <span style={{ fontSize: 12, padding: '4px 12px', borderRadius: 6, background: theme.colors.primary, color: '#fff', fontWeight: 600 }}>
            Set up
          </span>
        )}
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${theme.colors.border}` }}>
          {status?.provisioned ? (
            /* Already provisioned — show info */
            <div style={{ paddingTop: 12 }}>
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: theme.colors.textLight }}>Phone number</span>
                  <span style={{ fontWeight: 600, color: theme.colors.text, fontFamily: 'monospace' }}>{formatPhone(status.phone_number)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: theme.colors.textLight }}>Provisioned</span>
                  <span style={{ color: theme.colors.text }}>{formatDate(status.provisioned_at)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: theme.colors.textLight }}>Monthly cost</span>
                  <span style={{ color: theme.colors.text }}>$1.15/month (included in plan)</span>
                </div>
              </div>
              <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, background: '#F0FDF4', border: '1px solid #BBF7D0', fontSize: 12, color: '#065F46' }}>
                All agent texts are sent from this number. Leads see a local area code and can reply directly. Replies are routed to the last agent who texted them.
              </div>
            </div>
          ) : (
            /* Not provisioned — search and purchase flow */
            <div style={{ paddingTop: 12 }}>
              <div style={{ fontSize: 13, color: theme.colors.textLight, marginBottom: 12, lineHeight: 1.5 }}>
                Get a dedicated phone number for your brokerage. Your agents' texts will come from this number, and leads can reply directly. Choose a local NJ area code so leads see a familiar number.
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <input
                  value={areaCode}
                  onChange={e => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                  placeholder="Area code (e.g., 973)"
                  maxLength={3}
                  style={{
                    flex: 1, padding: '8px 12px', borderRadius: 8, fontSize: 14,
                    border: `1px solid ${theme.colors.border}`, background: theme.colors.card,
                    color: theme.colors.text, outline: 'none', fontFamily: 'monospace', letterSpacing: 2,
                  }}
                  onKeyDown={e => e.key === 'Enter' && searchNumbers()}
                />
                <button onClick={searchNumbers} disabled={searching || areaCode.length !== 3} style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none',
                  background: theme.colors.primary, color: '#fff', fontWeight: 600, fontSize: 13,
                  cursor: searching ? 'wait' : 'pointer', opacity: searching ? 0.6 : 1,
                }}>
                  {searching ? 'Searching...' : 'Search'}
                </button>
              </div>

              <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                {['201', '551', '609', '732', '848', '856', '862', '908', '973'].map(code => (
                  <button key={code} onClick={() => { setAreaCode(code); }}
                    style={{
                      padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                      border: `1px solid ${areaCode === code ? theme.colors.primary : theme.colors.border}`,
                      background: areaCode === code ? `${theme.colors.primary}10` : 'transparent',
                      color: areaCode === code ? theme.colors.primary : theme.colors.textLight,
                      cursor: 'pointer',
                    }}>
                    {code}
                  </button>
                ))}
              </div>

              {error && (
                <div style={{ padding: '8px 12px', borderRadius: 8, background: '#FEF2F2', border: '1px solid #FECACA', fontSize: 12, color: '#991B1B', marginBottom: 12 }}>
                  {error}
                </div>
              )}

              {searchResults && searchResults.length > 0 && (
                <div style={{ border: `1px solid ${theme.colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ padding: '8px 12px', background: '#F8FAFC', fontSize: 11, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Available numbers ({searchResults.length})
                  </div>
                  {searchResults.map((n, i) => (
                    <div key={n.phone_number} style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                      borderTop: i > 0 ? `1px solid ${theme.colors.border}` : 'none',
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text, fontFamily: 'monospace' }}>
                          {formatPhone(n.phone_number)}
                        </div>
                        {n.locality && (
                          <div style={{ fontSize: 11, color: theme.colors.textLight }}>{n.locality}, {n.region}</div>
                        )}
                      </div>
                      <button
                        onClick={() => purchaseNumber(n.phone_number)}
                        disabled={purchasing === n.phone_number}
                        style={{
                          padding: '6px 14px', borderRadius: 6, border: 'none',
                          background: theme.colors.primary, color: '#fff', fontSize: 12, fontWeight: 600,
                          cursor: purchasing ? 'wait' : 'pointer', opacity: purchasing === n.phone_number ? 0.6 : 1,
                        }}>
                        {purchasing === n.phone_number ? 'Purchasing...' : 'Select'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// NOTIFICATIONS TAB
// ══════════════════════════════════════════════════════════════
function NotificationsTab() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    (async () => {
      const h = await getHeaders();
      if (!h) return;
      const r = await fetch('/api/notifications?action=preferences', { headers: h });
      const d = await r.json();
      setPrefs(d.preferences);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    const h = await getHeaders();
    const resp = await fetch('/api/notifications?action=preferences', { method: 'POST', headers: h, body: JSON.stringify(prefs) });
    setMsg(resp.ok ? 'Saved!' : 'Error');
    setSaving(false);
  };

  if (!prefs) return <div style={{ color: theme.colors.textLight, padding: 32 }}>Loading...</div>;

  const channels = prefs.channel_settings || {};
  const types = Object.keys(channels);
  const channelNames = ['in_app', 'email', 'push', 'sms'];
  const typeLabels = {
    lead_assigned: 'Lead Assigned', lead_hot: 'Hot Lead Alert', task_due: 'Task Due',
    task_overdue: 'Task Overdue', deal_milestone: 'Deal Milestone', deal_message: 'Deal Room Message',
    automation_fired: 'Automation Fired', system: 'System Updates',
  };

  const toggleChannel = (type, channel) => {
    setPrefs(p => ({ ...p, channel_settings: { ...p.channel_settings, [type]: { ...p.channel_settings[type], [channel]: !p.channel_settings[type][channel] } } }));
  };

  return (
    <div>
      <div style={card}>
        <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 16 }}>Notification Channels</h2>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
              <th style={{ padding: '8px 0', textAlign: 'left', color: theme.colors.textLight, fontWeight: 500 }}>Event</th>
              {channelNames.map(ch => <th key={ch} style={{ padding: '8px 0', textAlign: 'center', color: theme.colors.textLight, fontWeight: 500, textTransform: 'capitalize' }}>{ch.replace('_', ' ')}</th>)}
            </tr>
          </thead>
          <tbody>
            {types.map(type => (
              <tr key={type} style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                <td style={{ padding: '10px 0', color: theme.colors.text }}>{typeLabels[type] || type}</td>
                {channelNames.map(ch => (
                  <td key={ch} style={{ padding: '10px 0', textAlign: 'center' }}>
                    <button onClick={() => toggleChannel(type, ch)} style={{
                      width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer', position: 'relative',
                      background: channels[type]?.[ch] ? theme.colors.primary : theme.colors.border, transition: 'background 0.2s',
                    }}>
                      <span style={{
                        display: 'block', width: 14, height: 14, borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: 3, left: channels[type]?.[ch] ? 19 : 3, transition: 'left 0.2s',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                      }} />
                    </button>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={card}>
        <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 12 }}>Email Digest</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <select value={prefs.email_digest_frequency || 'daily'} onChange={e => setPrefs(p => ({ ...p, email_digest_frequency: e.target.value }))} style={{ ...input, width: 130, cursor: 'pointer' }}>
            <option value="realtime">Realtime</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="none">Off</option>
          </select>
          <span style={{ fontSize: 13, color: theme.colors.textLight }}>at</span>
          <input type="time" value={prefs.digest_time || '08:00'} onChange={e => setPrefs(p => ({ ...p, digest_time: e.target.value }))} style={{ ...input, width: 120 }} />
        </div>
      </div>

      <div style={card}>
        <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 12 }}>Quiet Hours</h2>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
          <input type="checkbox" checked={prefs.quiet_hours_enabled || false} onChange={e => setPrefs(p => ({ ...p, quiet_hours_enabled: e.target.checked }))} />
          <span style={{ fontSize: 13, color: theme.colors.text }}>Enable quiet hours (no push/SMS)</span>
        </label>
        {prefs.quiet_hours_enabled && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="time" value={prefs.quiet_start || '22:00'} onChange={e => setPrefs(p => ({ ...p, quiet_start: e.target.value }))} style={{ ...input, width: 120 }} />
            <span style={{ fontSize: 13, color: theme.colors.textLight }}>to</span>
            <input type="time" value={prefs.quiet_end || '07:00'} onChange={e => setPrefs(p => ({ ...p, quiet_end: e.target.value }))} style={{ ...input, width: 120 }} />
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>{saving ? 'Saving...' : 'Save Preferences'}</button>
        {msg && <span style={{ fontSize: 13, color: theme.colors.success }}>{msg}</span>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// SECURITY TAB
// ══════════════════════════════════════════════════════════════
function SecurityTab() {
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('');
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);

  useEffect(() => {
    // Simulate loading session data (Supabase doesn't expose sessions list natively)
    setLoadingSessions(false);
    setSessions([
      { id: 1, device: 'Current session', ip: '—', lastActive: 'Now', current: true },
    ]);
  }, []);

  const changePassword = async () => {
    setMsg(''); setMsgType('');
    if (!newPwd || !confirmPwd) { setMsg('Please fill in all fields'); setMsgType('error'); return; }
    if (newPwd !== confirmPwd) { setMsg('Passwords do not match'); setMsgType('error'); return; }
    if (newPwd.length < 8) { setMsg('Password must be at least 8 characters'); setMsgType('error'); return; }

    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPwd });
      if (error) { setMsg(error.message); setMsgType('error'); }
      else { setMsg('Password updated successfully'); setMsgType('success'); setCurrentPwd(''); setNewPwd(''); setConfirmPwd(''); }
    } catch (e) { setMsg('Error updating password'); setMsgType('error'); }
    setSaving(false);
  };

  const signOutAllSessions = async () => {
    try {
      await supabase.auth.signOut({ scope: 'global' });
      setMsg('All sessions signed out. You will be redirected to login.');
      setMsgType('success');
    } catch { setMsg('Error signing out sessions'); setMsgType('error'); }
  };

  return (
    <div>
      {/* Change Password */}
      <div style={card}>
        <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 16 }}>Change Password</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 400 }}>
          <div>
            <label style={label}>Current Password</label>
            <input type="password" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} style={input} placeholder="Enter current password" />
          </div>
          <div>
            <label style={label}>New Password</label>
            <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} style={input} placeholder="At least 8 characters" />
          </div>
          <div>
            <label style={label}>Confirm New Password</label>
            <input type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} style={input} placeholder="Re-enter new password" />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <button onClick={changePassword} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
            {saving ? 'Updating...' : 'Update Password'}
          </button>
          {msg && <span style={{ fontSize: 13, color: msgType === 'success' ? theme.colors.success : theme.colors.danger }}>{msg}</span>}
        </div>
      </div>

      {/* Active Sessions */}
      <div style={card}>
        <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 16 }}>Active Sessions</h2>
        {sessions.map(s => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${theme.colors.border}` }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, color: theme.colors.text }}>{s.device}</div>
              <div style={{ fontSize: 12, color: theme.colors.textLight }}>IP: {s.ip} &middot; Last active: {s.lastActive}</div>
            </div>
            {s.current && <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 12, background: `${theme.colors.success}18`, color: theme.colors.success, fontWeight: 500 }}>This device</span>}
          </div>
        ))}
        <button onClick={signOutAllSessions} style={{ ...btnSecondary, marginTop: 16, color: theme.colors.danger, borderColor: `${theme.colors.danger}40` }}>
          Sign Out All Other Sessions
        </button>
      </div>

      {/* Account Actions */}
      <div style={card}>
        <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 8 }}>Account</h2>
        <p style={{ fontSize: 13, color: theme.colors.textLight, marginBottom: 16 }}>
          Need to delete your account or export your data? Contact support at support@trovva.ai.
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// SETUP WIZARD TAB — resume or re-run onboarding
// ══════════════════════════════════════════════════════════════
function SetupWizardTab({ showToast }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [showWizard, setShowWizard] = useState(false);

  // OnboardingFlow is statically imported (already in the main bundle via App.jsx)

  const loadStatus = async () => {
    const h = await getHeaders();
    if (!h) return;
    const resp = await fetch('/api/settings?section=onboarding_status', { headers: h });
    if (resp.ok) {
      const data = await resp.json();
      setStatus(data);
    }
    setLoading(false);
  };

  useEffect(() => { loadStatus(); }, []);

  const handleRerunWizard = async () => {
    setResetting(true);
    const h = await getHeaders();
    if (!h) { setResetting(false); return; }
    // Reset onboarding progress in DB
    const resp = await fetch('/api/settings?section=onboarding_reset', { method: 'POST', headers: h });
    if (!resp.ok) {
      showToast?.('Failed to reset setup wizard');
      setResetting(false);
      return;
    }
    setResetting(false);
    setShowWizard(true);
  };

  const progress = status?.onboarding_progress;
  const isCompleted = progress?.completed;
  const isDismissed = progress?.dismissed && !progress?.completed;
  const lastStep = progress?.step ?? 0;

  const checkItems = [
    { label: 'Profile complete', done: status?.profile_complete },
    { label: 'Branding set', done: status?.branding_set },
    { label: 'Email connected', done: status?.email_connected },
    { label: 'Contacts added', done: status?.has_contacts },
    { label: 'Deals created', done: status?.has_deals },
    { label: 'Team invited', done: status?.has_team },
    { label: 'Billing active', done: status?.billing_active },
  ];
  const completedCount = checkItems.filter(i => i.done).length;

  if (showWizard) {
    return (
      <OnboardingFlow
        user={{ id: 'settings-rerun' }}
        onComplete={() => {
          setShowWizard(false);
          showToast?.('Setup complete!');
          loadStatus();
        }}
        onExit={() => {
          setShowWizard(false);
          showToast?.('Setup paused — you can resume anytime.');
          loadStatus();
        }}
      />
    );
  }

  if (loading) return <p style={{ color: theme.colors.textLight, fontSize: 14 }}>Loading setup status...</p>;

  return (
    <div>
      {/* Status card */}
      <div style={card}>
        <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, marginBottom: 4 }}>
          Setup Wizard
        </h2>
        <p style={{ fontSize: 13, color: theme.colors.textLight, marginBottom: 16 }}>
          {isCompleted
            ? 'You\'ve completed the initial setup. You can re-run it anytime to update your configuration.'
            : isDismissed
              ? 'You paused the setup wizard earlier. Pick up where you left off or start fresh.'
              : 'Walk through the guided setup to configure your brokerage, branding, integrations, and team.'}
        </p>

        <button
          onClick={handleRerunWizard}
          disabled={resetting}
          style={{
            ...btnPrimary,
            opacity: resetting ? 0.6 : 1,
            cursor: resetting ? 'not-allowed' : 'pointer',
          }}
        >
          {resetting ? 'Preparing...' : isCompleted ? 'Re-run Setup Wizard' : isDismissed ? 'Resume Setup' : 'Start Setup Wizard'}
        </button>
      </div>

      {/* Checklist card */}
      <div style={card}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, marginBottom: 12 }}>
          Setup Checklist ({completedCount}/{checkItems.length})
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {checkItems.map(item => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 20, height: 20, borderRadius: '50%',
                background: item.done ? theme.colors.success : `${theme.colors.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, color: 'white', fontWeight: 700, flexShrink: 0,
              }}>
                {item.done ? '✓' : ''}
              </div>
              <span style={{
                fontSize: 14,
                color: item.done ? theme.colors.text : theme.colors.textLight,
                textDecoration: item.done ? 'none' : 'none',
              }}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
